import { ReactNode, useContext } from 'react'
import { ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Link } from 'libs/router'
import { format, parseISO } from 'libs/dates'
import { useTranslation } from 'libs/i18n'

import { routes } from 'config/routes'
import { CtaLink, Currency, TableImage } from 'shared/components'
import { Dialog } from 'shared/components/Dialog/Dialog'
import { useUserRoles } from 'shared/hooks'
import { StoreSettingsContext } from 'store'
import { cn } from 'utils/style'
import type { SaleItem } from 'features/sales/types/sales.types'

type SaleDetailsDialogProps = {
	sale: SaleItem | null
	closeModal: () => void
}

type RowProps = {
	label: string
	value: ReactNode
	emphasis?: 'bold' | 'success'
}

const Row = ({ label, value, emphasis }: RowProps) => (
	<div className="flex items-baseline justify-between gap-4 py-1.5">
		<dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
			{label}
		</dt>
		<dd
			className={cn(
				'text-right text-[13px] tabular-nums',
				emphasis ? 'font-display font-bold' : 'font-medium',
				emphasis === 'success' && 'text-success'
			)}>
			{value}
		</dd>
	</div>
)

const sectionClassName = 'rounded-md border border-border p-3.5'
const sectionTitleClassName =
	'mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground'

export const SaleDetailsDialog = ({ sale, closeModal }: SaleDetailsDialogProps) => {
	const { t } = useTranslation()
	const { isStaff } = useUserRoles()
	const storeCtx = useContext(StoreSettingsContext)

	if (!sale) return null

	const booth = sale.booking?.booth
	const boothLabel = booth?.booth_type?.name
		? `${booth.booth_type.name}${booth.number ? ` #${booth.number}` : ''}`
		: `#${sale.booking?.reference}`

	return (
		<Dialog
			isOpened
			ariaLabel={t('Sale details for {{ name }}', { name: sale.product_name })}
			closeModal={closeModal}>
			<div className="flex items-center gap-3 px-6 pb-4 pt-6">
				<TableImage className="h-10 w-10" src={sale.product_image} />
				<div className="min-w-0">
					<h2 className="truncate font-display text-lg font-bold tracking-tight">
						{sale.product_name}
					</h2>
					<p className="text-[11px] tabular-nums text-muted-foreground">
						{t('SKU {{ sku }} · Ref {{ reference }}', {
							sku: sale.product_sku,
							reference: sale.receipt_reference
						})}
					</p>
				</div>
				<button
					type="button"
					aria-label={t('Close')}
					className="ml-auto grid h-9 w-9 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
					onClick={closeModal}>
					<XMarkIcon className="h-4 w-4 stroke-2" />
				</button>
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
				<div className="grid gap-4 sm:grid-cols-2">
					{isStaff && (
						<section className={sectionClassName}>
							<h3 className={sectionTitleClassName}>{t('Customer')}</h3>
							<p className="text-[13px] font-semibold">{sale.account_name}</p>
							<p className="truncate text-[11px] text-muted-foreground">{sale.account_email}</p>
						</section>
					)}
					<section className={sectionClassName}>
						<h3 className={sectionTitleClassName}>{t('Booking')}</h3>
						{sale.booking?.id ? (
							<>
								<Link
									className="block text-[13px] font-semibold text-primary hover:underline"
									to={routes.bookingEdit(String(sale.booking.id))}>
									{boothLabel}
								</Link>
								<p className="text-[11px] tabular-nums text-muted-foreground">
									#{sale.booking.reference} {sale.booking.start_date}
								</p>
							</>
						) : (
							<p className="text-[13px] text-muted-foreground">—</p>
						)}
					</section>
				</div>

				<section className={cn(sectionClassName, 'mt-4')}>
					<h3 className={sectionTitleClassName}>{t('Sale')}</h3>
					<dl className="divide-y divide-border/60">
						<Row
							label={t('Date')}
							value={format(parseISO(String(sale.created)), 'dd.MM.yyyy HH:mm:ss')}
						/>
						<Row label={t('Quantity')} value={sale.quantity} />
						<Row label={t('Price')} value={<Currency price={sale.product_unit_price} />} />
						<Row label={t('Subtotal')} value={<Currency price={sale.subtotal} />} />
						{storeCtx?.storeSettings?.vat_enabled && (
							<Row
								label={t('VAT ({{ percentage }}%)', { percentage: sale.vat_percentage })}
								value={<Currency price={sale.vat} />}
							/>
						)}
						<Row label={t('Discount')} value={<Currency price={sale.discount} />} />
						<Row label={t('Total')} value={<Currency price={sale.total} />} emphasis="bold" />
						<Row
							label={t('Commission ({{ percentage }}%)', {
								percentage: +sale.commission_percentage
							})}
							value={<Currency price={sale.commission} />}
						/>
						<Row
							label={t('Net profit')}
							value={
								<Currency
									price={{ amount: Number(sale.net_profit), currency: sale.total.currency }}
								/>
							}
							emphasis="success"
						/>
						<Row label={t('Payout ID')} value={sale.payout ? `#${sale.payout}` : '—'} />
					</dl>
				</section>
			</div>

			{isStaff && (
				<div className="flex justify-end border-t border-border px-5 py-3">
					<CtaLink
						variant="outline"
						icon={<ArrowTopRightOnSquareIcon />}
						text={t('Open sale')}
						to={routes.saleEdit(String(sale.id))}
					/>
				</div>
			)}
		</Dialog>
	)
}
